import React, { type KeyboardEventHandler, useCallback, useEffect, useState, useRef } from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import { toJSON } from '@atlaskit/editor-common/utils';
// eslint-disable-next-line @atlaskit/editor/warn-no-restricted-imports
import type { EditorActions } from '@atlaskit/editor-core';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import { type JSONDocNode } from '@atlaskit/editor-json-transformer';
import { AllSelection } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import { fg } from '@atlaskit/platform-feature-flags';
import { Flex, Inline, Box } from '@atlaskit/primitives/compiled';
import { TextSerializer } from '@atlaskit/renderer/text-serializer';
import { token } from '@atlaskit/tokens';

import { AddItemButton } from '../components/AddItemButton/AddItemButton';
import { LinkPickerButton } from '../components/LinkPickerButton/LinkPickerButton';
import { MentionButton } from '../components/MentionButton/MentionButton';
import { createAIPromptPreset, type AIPromptPresetOptions } from '../presets/prompt';
import { hasHardBreakInADF } from '../utils';

import messages from './messages';
import type { PromptEditorType } from './types';

export type CreatePromptEditorOptions = Omit<AIPromptPresetOptions, 'placeholder' | 'onSave'> & {
	onSubmit?: (props: { adf: JSONDocNode | undefined; text: string | undefined }) => void;
};

const styles = cssMap({
	insertMenuContainer: {
		marginInlineStart: 'auto',
		marginBlockStart: 'auto',
	},
	editorBase: {
		marginBlockStart: token('space.050'),
		wordBreak: 'break-word',
		flexGrow: 1,
		flexShrink: 1,
		whiteSpace: 'pre-wrap',
		wordWrap: 'break-word',
		width: 'auto',
	},
	editorMultiline: {
		marginBlockStart: token('space.050'),
		wordBreak: 'break-word',
		flexGrow: 1,
		flexShrink: 1,
		whiteSpace: 'pre-wrap',
		wordWrap: 'break-word',
		width: '100%',
	},
});

// PromptEditorProps is mirrored at `packages/editor/generative-ai-modal/src/ui/components/PromptEditorWrapper/PromptEditorWrapper.tsx`
// since editor-ai-injected-editors cannot be a dependency of generative-ai-modal or editor-plugin-ai.
type PromptEditorProps = {
	ariaLabel?: string;
	autoFocus?: boolean;
	defaultValue?: JSONDocNode;
	/**
	 * We have tied enabling links to ConvoAI FF through this prop.
	 */
	enableLinks?: boolean;
	isInvalid?: boolean;
	onADFChange?: (input: JSONDocNode) => void;
	onBlur?: () => void;
	onChange?: (value: { adf: JSONDocNode; text: string }) => void;
	onFocus?: () => void;
	onInputChange?: (input: string) => void;
	placeholder?: string;
	/**
	 * Using separate setter function for clear as focus and blur
	 *  is set from editorApi while clear from editorActions.
	 * They both can be available at different time.
	 * So it's safest to have separate setter function.
	 */
	setClearHandler?: (clearFn?: () => boolean) => void;
	setFocusHandlers?: (focusFn?: () => boolean, blurFn?: () => boolean) => void;
	submit?: () => void;
	type?: PromptEditorType;
};

type EditorWrapperProps = { editorProps: PromptEditorProps; options: CreatePromptEditorOptions };

const EditorWrapper = (props: EditorWrapperProps) => {
	const appearance = 'chromeless';
	const { editorProps, options } = props;
	const {
		type = 'user-input',
		onChange,
		onInputChange,
		onADFChange,
		defaultValue,
		enableLinks,
		setFocusHandlers,
		setClearHandler,
		ariaLabel,
		submit,
	} = editorProps;
	const { onSubmit } = options;
	const adfRef = useRef<JSONDocNode | undefined>(undefined);
	const textRef = useRef<string | undefined>(undefined);

	const { formatMessage } = useIntl();

	const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
	const [hasLinebreak, setHasLinebreak] = useState(false);

	const handleChange = useCallback(
		(editorView: EditorView) => {
			const textSerializer = TextSerializer.fromSchema(editorView.state.schema);
			const text = textSerializer.serializeFragment(editorView.state.doc.content);
			const adf = toJSON(editorView.state.doc);

			onChange?.({ text, adf });
			onADFChange?.(adf);
			onInputChange?.(text);
			adfRef.current = adf;
			textRef.current = text;

			const hasHardBreak = hasHardBreakInADF(adf);
			setHasLinebreak(hasHardBreak);

			if (text) {
				setIsSubmitDisabled(false);
			} else {
				setIsSubmitDisabled(true);
			}
		},
		[onChange, onADFChange, onInputChange],
	);

	const onSave = useCallback(
		(editorView: EditorView) => {
			handleChange(editorView);
		},
		[handleChange],
	);

	const onSaveSubmit = useCallback(() => {
		if (onSubmit) {
			onSubmit?.({ adf: adfRef.current, text: textRef.current });
		} else {
			submit?.();
		}
	}, [submit, onSubmit]);

	// We could have created preset outside PromptEditor component,
	//  but currently placeholder prop ComposableEditor is not working.
	// We have to pass placeholder to placeholder plugin.
	// That's why using usePreset, who memoize preset.
	const { preset, editorApi } = usePreset(
		() =>
			createAIPromptPreset({
				...options,
				placeholder: { placeholder: editorProps.placeholder },
				onSave: fg('aifc_create_enabled') ? onSaveSubmit : onSave,
				enableLinks,
			}),
		[options, editorProps.placeholder],
	);

	const editorViewRef = useRef<EditorView | null>(null);

	const onEditorReady = useCallback(
		(editorActions: EditorActions) => {
			if (setClearHandler) {
				setClearHandler(() => editorActions.clear());
			}
			const editorView = editorActions._privateGetEditorView();
			if (editorView) {
				editorViewRef.current = editorView;

				if (!editorActions.isDocumentEmpty()) {
					editorView.dispatch(
						editorView.state.tr.setSelection(new AllSelection(editorView.state.doc)),
					);
				}
			}
		},
		[setClearHandler],
	);

	useEffect(() => {
		if (setFocusHandlers) {
			/**
			 * We need to expose clear function through ref because
			 *  changing defaultValue does not clear editor.
			 */
			setFocusHandlers(editorApi?.core?.actions.blur, editorApi?.core?.actions.focus);
		}
	}, [editorApi, setFocusHandlers]);

	// Skip any toolbars that displays in the editor, like Link Picker toolbar
	// Yes, it's ugly, but it's easier way, because we can't have access to the PromptEditor
	// floating toolbar.
	const stopPropagationForFloatingToolbar: KeyboardEventHandler = useCallback((event) => {
		const eventFloatingToolbar =
			event.target instanceof HTMLElement
				? event.target.closest('[data-testid="editor-floating-toolbar"]')
				: null;

		// Check that floating toolbar is a child of the current target,
		// i.e. it's a child of current the editor.
		if (eventFloatingToolbar && event.currentTarget.contains(eventFloatingToolbar)) {
			event.stopPropagation();
		}
	}, []);

	const addItemButton = <AddItemButton editorApi={editorApi} promptType={type} />;
	const submitButton = (
		<IconButton
			testId="submit-icon-button"
			shape="circle"
			appearance="primary"
			icon={ArrowUpIcon}
			isDisabled={isSubmitDisabled}
			onClick={fg('aifc_create_enabled') ? onSaveSubmit : submit}
			isTooltipDisabled={isSubmitDisabled}
			label={formatMessage(messages.promptSubmit)}
			tooltip={{ position: 'top' }}
		/>
	);

	const editor = (
		<ComposableEditor
			mentionProvider={options.mentionProvider}
			appearance={appearance}
			minHeight={20}
			maxHeight={200}
			preset={preset}
			assistiveLabel={ariaLabel ?? formatMessage(aiMessages.promptbuilderAriaLabel)}
			shouldFocus={editorProps.autoFocus ?? true}
			defaultValue={defaultValue}
			onChange={handleChange}
			onEditorReady={onEditorReady}
		/>
	);

	return (
		// eslint-disable-next-line @atlassian/a11y/no-static-element-interactions
		<div onKeyDown={stopPropagationForFloatingToolbar} onKeyUp={stopPropagationForFloatingToolbar}>
			<Flex
				gap="space.050"
				/**
				 * Note wrap and direction is purely added for the new modals multiline behaviour
				 */
				wrap={fg('aifc_create_enabled') ? 'wrap' : undefined}
				direction={fg('aifc_create_enabled') ? 'row' : undefined}
			>
				{fg('aifc_create_enabled') ? (
					<Box xcss={hasLinebreak ? styles.editorMultiline : styles.editorBase}>{editor}</Box>
				) : (
					editor
				)}

				{fg('aifc_create_enabled') ? (
					<Inline xcss={styles.insertMenuContainer} space="space.050">
						{[addItemButton, submitButton]}
					</Inline>
				) : (
					(type === 'user-input' || type === 'interrogate') && (
						<>
							{options.enableLinks && options.linkPicker && (
								<LinkPickerButton editorApi={editorApi} promptType={type} />
							)}
							{options.mentionProvider && <MentionButton editorApi={editorApi} promptType={type} />}
						</>
					)
				)}
			</Flex>
		</div>
	);
};

export function createPromptEditor(options: CreatePromptEditorOptions) {
	return (props: PromptEditorProps): JSX.Element => {
		const editorProps = { editorProps: props, options };

		return (
			<EditorContext>
				<EditorWrapper
					// Ignored via go/ees005
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...editorProps}
				/>
			</EditorContext>
		);
	};
}
